import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { MultiSelect } from "primereact/multiselect";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { FormInput } from "../../../components/FormInput";
import NoInfo from "../../../components/NoInfo";
import PrimeTableFooter from "../../../components/PrimeTableFooter";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { checkLoading, SystemLoading } from "../../../store/loadingStore";
import { handleSuccessMessage, MessageContext } from "../../../store/messageStore";
import { queryClassNoTypeList, queryMaterialList } from "../../../utility/service/carbon-service";
import {
  queryEmissionSourceFactorDetail,
  queryEmissionSourceFactorList,
  queryEmissionSourceList,
  updateEmissionSourceSort,
} from "../../../utility/service/emission-factor-service";
import { checkDpSuccess, getFormErrorMessage } from "../../../utility/service/tool-service";

const EmissionSource = () => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext);
  const [gasRateSourceList, setGasRateSourceList] = useState([]);
  const [lastSeq, setLastSeq] = useState("");
  const [data, setData] = useState([]);
  const [DetailDialog, setDetailDialog] = useState(false);
  const [materialCodeList, setMaterialCodeList] = useState([]);
  const menu = useRef(null);
  const [action, setAction] = useState("");
  const sensors = useSensors(useSensor(PointerSensor));
  const isEditable = useEditableFlag("B20501");
  const [oriEmissionList, setOriEmissionList] = useState([]);
  const [emissionList, setEmissionList] = useState([]);

  const items = [
    {
      label: `${t("button.edit")}${t("button.sort")}`,
      icon: "pi pi-sort-alt",
      disabled: !isEditable,
      command: () => {
        setAction("sort");
      },
    },
  ];

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    axios_queryClassNoTypeList();
    axios_queryEmissionSourceList();
    axios_queryMaterialList();
  }, []);

  useEffect(() => {
    if (oriEmissionList.length > 0) {
      axios_queryEmissionSourceFactorList();
    }
  }, [oriEmissionList]);

  // 查詢排放源來源
  const axios_queryEmissionSourceList = () => {
    checkLoading(loading);
    queryEmissionSourceList()
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data?.RespBody?.gasRateSourceList;
          setGasRateSourceList(data);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 查詢排放源係數清單
  const axios_queryEmissionSourceFactorList = (reqBody = {}, readMore = false) => {
    checkLoading(loading);
    queryEmissionSourceFactorList(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res?.data.RespBody?.cfvPlanEmissionSourceList.map((item, index) => {
            let emissionTag = [];

            // 找到 oriEmissionList 中對應的 type
            const oriItem = oriEmissionList.find((o) => o.type === item.type);
            if (oriItem) {
              if (oriItem.subTypeList && oriItem.subTypeList.length > 0 && item.subType) {
                // 有 subTypeList → 拆每個 subType
                const subTypes = item.subType.split("");
                // 每個 subType 組成 type+subType
                emissionTag = subTypes.map((st) => item.type + st);
              } else {
                // 沒有 subTypeList → 直接用 type
                emissionTag = [item.type];
              }
            }

            return {
              ...item,
              emissionTag,
              materialName: materialCodeList.find((p) => p.materialCode === item.materialCode)?.materialName,
            };
          });
          if (data.length > 0) {
            setLastSeq(data[data.length - 1].sourceSeq);
            setData((prev) => (readMore ? [...prev, ...data] : [...data]));
          } else if (!readMore) {
            setData([]);
          }
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 查詢排放源清單
  const axios_queryClassNoTypeList = async () => {
    const classNoArray = Array.from({ length: 6 }, (_, i) => i + 1);
    const allResults = [];

    for (const classNo of classNoArray) {
      const res = await queryClassNoTypeList({ classNo });
      if (checkDpSuccess(res)) {
        allResults.push(res);
      }
    }

    const mergedList = allResults.flatMap((res) => buildTypeOptions(res?.data?.RespBody?.typeList) || []);

    const oriMergedList = allResults.flatMap((res) => res?.data?.RespBody?.typeList || []);

    setEmissionList(mergedList);
    setOriEmissionList(oriMergedList);
  };

  const buildTypeOptions = (typeList = []) => {
    return typeList.flatMap((typeItem) => {
      const { type, typeName, subTypeList } = typeItem;

      // 沒有 subTypeList
      if (!subTypeList || subTypeList.length === 0) {
        return [
          {
            key: type,
            label: typeName,
          },
        ];
      }

      // 有 subTypeList → 展開
      return subTypeList.map((sub) => ({
        key: `${type}${sub.subType}`,
        label: `${sub.typeName}`,
      }));
    });
  };

  // 查看更多
  const btnReadMore = () => {
    let reqBody = {
      subitemNo: getValues("subitemNo"),
      sourceSeq: lastSeq,
    };
    axios_queryEmissionSourceFactorList(reqBody, true);
  };

  //避免觸發 按enter 後 就觸發 onSubmit 事件冒泡 給input 使用 <FormInput enterClick事件 ; 一般input onKeyDown事件
  const inputCheckKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <Button
          tooltip={t(`button.detail`)}
          tooltipOptions={{ position: "top" }}
          rounded
          text
          severity="secondary"
          className="p-button-icon-only"
          onClick={() => {
            actionItem(rowData, "detail");
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-eye" />
        </Button>
      );
    }
  };

  const actionItem = (data = {}, action = "detail") => {
    if (action === "detail") {
      setDetailDialog(true);
      axios_queryEmissionSourceFactorDetail({
        sourceSeq: data.sourceSeq,
      });
    }
  };

  // 查詢排放係數來源詳情
  const axios_queryEmissionSourceFactorDetail = (reqBody = {}) => {
    checkLoading(loading);
    queryEmissionSourceFactorDetail(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody;
          setValue("editSubitemNo", getValues("subitemNo"));
          setValue("sourceSeq", data.sourceSeq);
          setValue("type", data.type);
          setValue("subType", data.subType);
          setValue("sourceClass", data.sourceClass);
          setValue("sourceCode", data.sourceCode);
          setValue("materialCode", data.materialCode);
          setValue("sourceName", data.sourceName);
          setValue("sourceNameEnUs", data.sourceNameEnUs);
          setValue("sourceNameLocale2", data.sourceNameLocale2);
          setValue("sort", data.sort);
          setValue("gasRateSource", data.gasRateSource);
          setValue("cfvPlanEmissionSourceDetailList", data.cfvPlanEmissionSourceDetailList);
          let emissionTag = [];

          // 找到 oriEmissionList 中對應的 type
          const oriItem = oriEmissionList.find((o) => o.type === data.type);
          if (oriItem) {
            if (oriItem.subTypeList && oriItem.subTypeList.length > 0 && data.subType) {
              // 有 subTypeList → 拆每個 subType
              const subTypes = data.subType.split("");
              // 每個 subType 組成 type+subType
              emissionTag = subTypes.map((st) => data.type + st);
            } else {
              // 沒有 subTypeList → 直接用 type
              emissionTag = [data.type];
            }
          }
          setValue("emissionTag", emissionTag);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 原燃物料code清單查詢
  const axios_queryMaterialList = () => {
    queryMaterialList().then((res) => {
      if (checkDpSuccess(res)) {
        setMaterialCodeList(res?.data?.RespBody?.materialList);
      }
    });
  };

  const onSubmit = (data) => {
    const reqBody = {
      subitemNo: data.subitemNo,
      keywordSearch: data.keywordSearch,
    };
    axios_queryEmissionSourceFactorList(reqBody);
  };

  const onEditSubmit = (data) => {};

  const SortableRow = ({ rowData, field, rowIndex, isButton, isSelect = false }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
      id: rowData.sourceSeq,
    });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
      position: "relative",
      zIndex: isDragging ? 9999 : "auto",
    };

    return (
      <tr ref={setNodeRef} style={style} {...attributes}>
        {/* 拖動手把 */}
        <td className="text-ellipsis">
          {isButton && (
            <Button
              className="p-button-icon-only"
              icon="pi pi-bars"
              rounded
              text
              style={{ cursor: "grab" }}
              ref={setActivatorNodeRef}
              {...listeners}
            />
          )}
          {isSelect && (
            <MultiSelect
              filter
              showClear
              className="w-full no-border-dropdown"
              disabled={true}
              style={{ width: "100%" }}
              display="chip"
              options={emissionList}
              optionLabel={"label"}
              optionValue={"key"}
              value={rowData[field]}
            />
          )}
          {!isButton && !isSelect && (rowIndex ?? rowData[field])}
        </td>
      </tr>
    );
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((p) => p.sourceSeq === active.id);
    const newIndex = data.findIndex((p) => p.sourceSeq === over.id);

    const newList = arrayMove(data, oldIndex, newIndex);

    setData(newList);
  };

  const handleSort = () => {
    const sortData = data.map((item, index) => ({
      sourceSeq: item.sourceSeq,
      sort: index + 1,
    }));
    const reqBody = {
      gwpList: sortData,
    };
    updateEmissionSourceSort(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        handleSuccessMessage(dispatch, t("button.sort") + " " + t("message.success"));
        axios_queryEmissionSourceFactorList();
      }
    });
  };

  return (
    <div>
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid">
              <div className="col-6">
                <label htmlFor="subitemNo" className="form-label">
                  {t("gasRateSource")}
                </label>
                <Controller
                  name="subitemNo"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      filter
                      showClear
                      appendTo={"self"}
                      optionLabel="subitemName"
                      optionValue="subitemNo"
                      value={field.value}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs_all")}
                      name="subitemNo"
                      control={control}
                      options={gasRateSourceList}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                      }}
                    />
                  )}
                />
                {getFormErrorMessage(errors, "subitemNo")}
              </div>
              <div className="col-6">
                <FormInput
                  register={register}
                  errors={errors}
                  id="keywordSearch"
                  type="text"
                  labelClass={`control-label`}
                  enterClick={inputCheckKeyDown}
                  labelText={t("keyword")}
                />
              </div>
            </div>
            <div className="col-12 gap-2 flex-all-center">
              <Button type="submit" label={t("button.query")} />
            </div>
          </form>
        </div>
      </div>

      <div className="flex gap-2 align-items-center justify-content-center w-full">
        {action === "" && (
          <div className="ml-auto">
            <Menu model={items} popup ref={menu} />
            <Button
              className="p-button-icon-only"
              onClick={(event) => menu.current.toggle(event)}
              icon="pi pi-ellipsis-h"
              aria-controls="popup_menu"
              rounded
              text
              aria-haspopup
            />
          </div>
        )}
        {action !== "" && (
          <div className="ml-auto">
            {/* 存檔 */}
            {action === "sort" && (
              <Button
                className="p-button-icon-only"
                icon="pi pi-save"
                onClick={() => {
                  if (action === "sort") {
                    handleSort();
                  }
                  setAction("");
                }}
                disabled={!isEditable}
                tooltip={t("button.save")}
                tooltipOptions={{ position: "top" }}
                type="button"
                rounded
                text
                size="small"
              />
            )}
            {/* 取消 */}
            <Button
              className="p-button-icon-only"
              icon="pi pi-times"
              onClick={() => {
                setAction("");
              }}
              tooltip={t("button.cancel")}
              tooltipOptions={{ position: "top" }}
              type="button"
              rounded
              text
              size="small"
            />
          </div>
        )}
      </div>

      <div className="p-card">
        <div className="p-card-body">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={data.map((p) => p.sourceSeq)} strategy={verticalListSortingStrategy}>
              <DataTable
                stripedRows
                className="white-data-table hover-highlight no-shadow"
                emptyMessage={<NoInfo panel="table" />}
                value={data}
                footer={
                  data?.length > 0 && (
                    <PrimeTableFooter rowDataList={data} isShowMore={true} btnReadMore={btnReadMore} />
                  )
                }
              >
                {action === "sort" && (
                  <Column
                    style={{ width: "5%" }}
                    body={(rowData) => <SortableRow rowData={rowData} isButton={true} />}
                  />
                )}
                <Column
                  field="emissionTag"
                  header={t("emission_source_type")}
                  bodyStyle={{ maxWidth: "250px" }}
                  body={(rowData, { rowIndex }) =>
                    action === "sort" ? (
                      <SortableRow rowData={rowData} isButton={false} isSelect={true} field={"emissionTag"} />
                    ) : (
                      <MultiSelect
                        filter
                        showClear
                        className="w-full no-border-dropdown"
                        disabled={true}
                        style={{ width: "100%" }}
                        display="chip"
                        options={emissionList}
                        optionLabel={"label"}
                        optionValue={"key"}
                        value={rowData.emissionTag}
                      />
                    )
                  }
                />
                <Column
                  field="sourceClass"
                  header={t("emissionSourceClass")}
                  body={(rowData, { rowIndex }) =>
                    action === "sort" ? (
                      <SortableRow rowData={rowData} isButton={false} field={"sourceClass"} />
                    ) : (
                      rowData.sourceClass
                    )
                  }
                />
                <Column
                  field="sourceName"
                  header={t("emissionName")}
                  body={(rowData, { rowIndex }) =>
                    action === "sort" ? (
                      <SortableRow rowData={rowData} isButton={false} field={"sourceName"} />
                    ) : (
                      rowData.sourceName
                    )
                  }
                />
                <Column
                  field="materialName"
                  header={t("rawMaterial")}
                  body={(rowData, { rowIndex }) =>
                    action === "sort" ? (
                      <SortableRow rowData={rowData} isButton={false} field={"materialName"} />
                    ) : (
                      rowData.materialName
                    )
                  }
                />
                {action === "" && <Column field="sort" header={t("order")} />}
                {action === "" && <Column header={t("action_type")} body={actionBodyTemplate} />}
              </DataTable>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <PrimeDialog
        size="lg"
        visible={DetailDialog}
        onClose={() => {
          setDetailDialog(false);
        }}
        isShowConfBtn={false}
        title={t("button.detail")}
        bodyTemplate={
          <form onSubmit={handleSubmit(onEditSubmit)}>
            <div className="grid">
              {/* 排放源代碼 */}
              <div className="col-4">
                <FormInput
                  disabled={true}
                  register={register}
                  errors={errors}
                  id={"sourceCode"}
                  name={"sourceCode"}
                  type="text"
                  labelClass={`control-label`}
                  labelText={t("emissionSourceCode")}
                  enterClick={inputCheckKeyDown}
                />
              </div>
              {/* 排放係數來源 */}
              <div className="col-4">
                <label htmlFor="gasRateSource" className="form-label">
                  {t("gasRateSource")}
                </label>
                <Controller
                  name="gasRateSource"
                  control={control}
                  rules={{ required: { value: true, message: t("validation.required") } }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      filter
                      showClear
                      disabled={true}
                      appendTo={"self"}
                      optionLabel="subitemName"
                      optionValue="subitemNo"
                      value={getValues("gasRateSource")}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="gasRateSource"
                      control={control}
                      options={gasRateSourceList}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                      }}
                    />
                  )}
                />
                {getFormErrorMessage(errors, "gasRateSource")}
              </div>
              {/* 排放源型式 */}
              <div className="col-4">
                <label htmlFor="emissionTag" className="form-label">
                  {t("emission_source_type")}
                </label>
                <Controller
                  name={"emissionTag"}
                  control={control}
                  render={({ field, fieldState }) => (
                    <MultiSelect
                      filter
                      showClear
                      disabled={true}
                      className="w-full no-border-dropdown"
                      style={{ width: "100%" }}
                      display="chip"
                      options={emissionList}
                      optionLabel={"label"}
                      optionValue={"key"}
                      value={field.value}
                    />
                  )}
                />
              </div>
              {/* 排放源型式 */}
              <div className="col-4">
                <FormInput
                  register={register}
                  errors={errors}
                  disabled={true}
                  id={"type"}
                  name={"type"}
                  type="text"
                  labelClass={`control-label`}
                  labelText={t("emission_source_type")}
                  enterClick={inputCheckKeyDown}
                />
              </div>
              {/* 排放源子型式 */}
              <div className="col-4">
                <FormInput
                  register={register}
                  errors={errors}
                  disabled={true}
                  id={"subType"}
                  name={"subType"}
                  type="text"
                  labelClass={`control-label`}
                  labelText={t("emissionSourceSubType")}
                  enterClick={inputCheckKeyDown}
                />
              </div>
              {/* 原燃物料 */}
              <div className="col-4">
                <label htmlFor="materialCode" className="form-label">
                  {t("rawMaterial")}
                </label>
                <Controller
                  name="materialCode"
                  control={control}
                  rules={{ required: { value: true, message: t("validation.required") } }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      filter
                      showClear
                      disabled={true}
                      appendTo={"self"}
                      optionLabel="materialName"
                      optionValue="materialCode"
                      value={getValues("materialCode")}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="materialCode"
                      control={control}
                      options={materialCodeList}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                      }}
                    />
                  )}
                />
                {getFormErrorMessage(errors, "subitemNo")}
              </div>
              {/* 排放源(zh-TW) */}
              <div className="col-4">
                <FormInput
                  disabled={true}
                  register={register}
                  errors={errors}
                  id={"sourceName"}
                  name={"sourceName"}
                  type="text"
                  labelClass={`control-label`}
                  labelText={`${t("emissionName")}(zh-TW)`}
                  enterClick={inputCheckKeyDown}
                />
              </div>
              {/* 排放源(en-US) */}
              <div className="col-4">
                <FormInput
                  register={register}
                  errors={errors}
                  disabled={true}
                  id={"sourceNameEnUs"}
                  name={"sourceNameEnUs"}
                  type="text"
                  labelClass={`control-label`}
                  labelText={`${t("emissionName")}(en-US)`}
                  enterClick={inputCheckKeyDown}
                />
              </div>
              {/* 排放源(vi-VN) */}
              <div className="col-4">
                <FormInput
                  register={register}
                  errors={errors}
                  disabled={true}
                  id={"sourceNameLocale2"}
                  name={"sourceNameLocale2"}
                  type="text"
                  labelClass={`control-label`}
                  labelText={`${t("emissionName")}(vi-VN)`}
                  enterClick={inputCheckKeyDown}
                />
              </div>
              {/* 氣體 */}
              <div className="col-12">
                <DataTable
                  stripedRows
                  resizableColumns
                  value={getValues("cfvPlanEmissionSourceDetailList")}
                  rowGroupMode="rowspan"
                  groupRowsBy={["heatingValue", "heatingUnit"]}
                >
                  <Column field="gasCode" header={t("gasCode")} />
                  <Column
                    field="emissionFactorKgPerTj"
                    header={`${t("emissionFactorKgPerTJ")}(kg/TJ)`}
                    className="text-right"
                  />
                  <Column
                    field="emissionFactorKgPerKcal"
                    header={`${t("emissionFactorKgPerTJ")}(kg/kcal)`}
                    className="text-right"
                  />
                  <Column field="heatingValue" header={t("heatingValue")} className="text-right" />
                  <Column field="heatingUnit" header={`${t("heatingValue")}${t("unit")}`} />
                  <Column field="carbonRate" header={t("carbonRate")} className="text-right" />
                  <Column field="gasRate" header={t("gasRate")} className="text-right" />
                  <Column field="gasRateUnit" header={`${t("gasRate")}${t("unit")}`} />
                  <Column field="gasRateLower" header={t("gasRateLower")} className="text-right" />
                  <Column field="gasRateUpper" header={t("gasRateUpper")} className="text-right" />
                </DataTable>
              </div>
            </div>
          </form>
        }
      />
    </div>
  );
};

export default EmissionSource;
